import React, { useState } from "react";
import { Button, Card, Space, Typography, Pagination, Input } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import updateNoteRule from "../../utils/preferences/updateNoteRule";
import deleteNoteRule from "../../utils/preferences/deleteNoteRule";
import { toast } from 'react-hot-toast';
import { logEvent } from '../../analytics';

const { Text } = Typography;
const { TextArea } = Input;

const NoteRulesList = ({ 
  rules: initialRules, 
  pagination,
  onPageChange,
  onRulesChange
}) => {
  const { t } = useTranslation();
  const [rules, setRules] = useState(initialRules);
  const [inlineEditId, setInlineEditId] = useState(null);
  const [inlineEditText, setInlineEditText] = useState("");

  const handleInlineEdit = (rule) => {
    setInlineEditId(rule.getUUID());
    setInlineEditText(rule.getInstruction());
  };

  const handleInlineSave = async () => {
    if (inlineEditId && inlineEditText.trim()) {
      try {
        const updatedRule = await updateNoteRule(inlineEditId, inlineEditText.trim());
        const newRules = rules.map((rule) => 
          rule.getUUID() === inlineEditId ? updatedRule : rule
        );
        setRules(newRules);
        onRulesChange?.(newRules);
        setInlineEditId(null);
        setInlineEditText("");
        toast.success(t('common.saved'));
        logEvent('NoteRules', 'Edit');
      } catch (error) {
        toast.error(t('note_rules.errors.save'));
      }
    }
  };

  const handleInlineCancel = () => {
    setInlineEditId(null);
    setInlineEditText("");
  };

  const handleDeleteRule = async (uuid) => {
    try {
      await deleteNoteRule(uuid);
      const newRules = rules.filter((rule) => rule.getUUID() !== uuid);
      setRules(newRules);
      onRulesChange?.(newRules);
      toast.success(t('common.deleted'));
      logEvent('NoteRules', 'Delete');
    } catch (error) {
      toast.error(t('note_rules.errors.save'));
    }
  };

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      {rules.map((rule) => (
        <Card 
          key={rule.getUUID()}
          styles={{
            body: { 
              padding: inlineEditId === rule.getUUID() ? '16px' : '14px'
            }
          }}
          style={{ 
            backgroundColor: inlineEditId === rule.getUUID() ? '#ffffff' : '#fafafa',
            border: "1px solid #e8e8e8",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)"
          }}
        >
          {inlineEditId === rule.getUUID() ? (
            <Space direction="vertical" style={{ width: "100%" }}>
              <TextArea
                data-testid={`edit-textarea-${rule.getUUID()}`}
                value={inlineEditText}
                onChange={(e) => setInlineEditText(e.target.value)}
                autoSize={{ minRows: 3, maxRows: 6 }}
                style={{ backgroundColor: '#f5f5f5', padding: '16px', fontSize: '16px' }}
              />
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                <Button 
                  data-testid={`cancel-button-${rule.getUUID()}`}
                  onClick={handleInlineCancel}
                >
                  {t('common.cancel')}
                </Button>
                <Button 
                  data-testid={`save-button-${rule.getUUID()}`}
                  type="primary" 
                  onClick={handleInlineSave}
                >
                  {t('common.save')}
                </Button>
              </div>
            </Space>
          ) : (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Text style={{ flex: 1, marginRight: "12px", fontSize: '15px' }} data-testid={`rule-text-${rule.getUUID()}`}>
                <i>{rule.getInstruction()}</i>
              </Text>
              <Space size="small">
                <Button
                  data-testid={`edit-button-${rule.getUUID()}`}
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => handleInlineEdit(rule)}
                  size="small"
                />
                <Button
                  data-testid={`delete-button-${rule.getUUID()}`}
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => handleDeleteRule(rule.getUUID())}
                  size="small"
                />
              </Space>
            </div>
          )}
        </Card>
      ))}
      {pagination.total > pagination.pageSize && (
        <div style={{ marginTop: "12px", textAlign: "center" }}>
          <Pagination
            data-testid="pagination"
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            onChange={onPageChange}
            size="small"
          />
        </div>
      )}
    </Space>
  );
};

export default NoteRulesList; 